import { Injectable } from '@angular/core';
import { FileOpener } from '@ionic-native/file-opener/ngx';
import { FileTransfer, FileTransferObject } from '@ionic-native/file-transfer/ngx';
import { Platform } from '@ionic/angular';
import { PopupService } from './popup.service';
import { TalentJobFormatService } from 'src/modules/talent-dashboard/services/talent-job-format.service';
import { File } from '@ionic-native/file/ngx';
import * as JSZip from 'jszip';

@Injectable({
  providedIn: 'root'
})
export class DownloadService {
  constructor(
    private platform: Platform,
    private transfer: FileTransfer,
    private fileopen: FileOpener,
    private file: File,
    private popupService: PopupService,
    private talentJobFormatService: TalentJobFormatService
  ) {}

  async downloadPdf(pdfData: string, fileName: string) {
    const linkSource = `data:application/pdf;base64,${pdfData}`;
    const downloadLink = document.createElement('a');
    const completefileName = `${fileName}.pdf`;

    if ((this.platform.is('android') || this.platform.is('ios') || this.platform.is('ipad')) && !this.talentJobFormatService.isBrowserCheck()) {
      const fileTransfer: FileTransferObject = this.transfer.create();

      const path = this.file.dataDirectory;

      fileTransfer
        .download(linkSource, path + completefileName)
        .then(entry => {
          this.popupService.showModal(
            {
              heading: 'Success',
              message: 'Download complete!',
              btn: 'Open',
              navigateRoute: null,
              imgURL: 'assets/images/thumbs-up.png'
            },
            () => {
              this.fileopen.open(path + completefileName, 'application/pdf');
            }
          );
        })
        .catch(err => {
          console.log(err);
          alert('Error saving file: ' + err.message);
        });
    } else {
      // If downloaded by Web Browser or ios
      downloadLink.href = linkSource;
      downloadLink.download = fileName;
      downloadLink.click();
    }
  }

  async downloadCertificatesAsZip(userId: string, certificates: any[]) {
  try {
    if (!certificates || !Array.isArray(certificates) || certificates.length === 0) {
      this.popupService.showModal({
        heading: 'No Certificates',
        message: 'No certificates available to download.',
        btn: 'Ok',
        navigateRoute: null,
        imgURL: 'assets/images/sorry1.png'
      });
      return;
    }

    const filePromises: Promise<{ filename: string; blob: Blob }>[] = [];
    
    // Group certificates by type
    const certificatesByType = {};
    certificates.forEach(cert => {
      if (!certificatesByType[cert.type]) {
        certificatesByType[cert.type] = [];
      }
      certificatesByType[cert.type].push(cert);
    });

    let fileCount = 0;

    // Collect all certificate files
    Object.keys(certificatesByType).forEach(certType => {
      const certsOfType = certificatesByType[certType];
      
      certsOfType.forEach((cert, index) => {
        if (!cert.url || cert.markCertificateAsRejected?.isDeleted) {
          return;
        }

        fileCount++;
        
        const promise = fetch(cert.url)
          .then(response => response.blob())
          .then(blob => {
            const extension = cert.url.split('.').pop().split('?')[0] || 'jpg';
            const safeType = cert.type.replace(/[^a-zA-Z0-9]/g, '_');
            const filename = `${fileCount}_${safeType}_${index + 1}.${extension}`;
            return { filename, blob };
          })
          .catch(error => {
            console.error(`Error fetching certificate:`, error);
            return null;
          });
        
        filePromises.push(promise);
      });
    });

    if (filePromises.length === 0) {
      this.popupService.showModal({
        heading: 'No Certificates',
        message: 'No valid certificates available to download.',
        btn: 'Ok',
        navigateRoute: null,
        imgURL: 'assets/images/notice.png'
      });
      return;
    }

    const files = await Promise.all(filePromises);
    const validFiles = files.filter(file => file !== null);
    
    if (validFiles.length === 0) {
      this.popupService.showModal({
        heading: 'Download Failed',
        message: 'Failed to download certificates. Please try again.',
        btn: 'Dismiss',
        navigateRoute: null,
        imgURL: 'assets/images/sorry1.png'
      });
      return;
    }
    
    // Create ZIP
    const zip = new JSZip();
    validFiles.forEach(file => {
      zip.file(file.filename, file.blob);
    });

    const zipBlob = await zip.generateAsync({ 
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: { level: 6 }
    });

    const timestamp = new Date().getTime();
    const zipFilename = `certificates_user_${userId}_${timestamp}.zip`;

    // Save based on platform
    if ((this.platform.is('android') || this.platform.is('ios') || this.platform.is('ipad')) && 
        !this.talentJobFormatService.isBrowserCheck()) {
      // Native app
      const path = this.file.dataDirectory;

      await this.file.writeFile(path, zipFilename, zipBlob, { replace: true })
        .then(() => {
          this.popupService.showModal(
            {
              heading: 'Success',
              message: `Certificates downloaded successfully!`,
              btn: 'Open',
              navigateRoute: null,
              imgURL: 'assets/images/thumbs-up.png'
            },
            () => {
              this.fileopen.open(path + zipFilename, 'application/zip');
            }
          );
        })
        .catch(err => {
          console.log(err);
          alert('Error saving file: ' + err.message);
        });

    } else {
      // Web browser
      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(zipBlob);
      downloadLink.download = zipFilename;
      downloadLink.click();
      
      this.popupService.showModal({
        heading: 'Success',
        message: `Certificates downloaded successfully!`,
        btn: 'Ok',
        navigateRoute: null,
        imgURL: 'assets/images/thumbs-up.png'
      });
    }

  } catch (error) {
    console.error('Error downloading certificates:', error);
    this.popupService.showModal({
      heading: 'Sorry',
      message: 'Failed to download certificates. Please try again.',
      btn: 'Dismiss',
      navigateRoute: null,
      imgURL: 'assets/images/sorry1.png'
    });
  }
}
}
